/**
 * AI CONTEXT
 * Purpose: threaded comment/event feed for an evaluation with real-time updates.
 * Reads: eval comments subscription; Writes: new comments + eval comment notifications.
 * Used by: EvaluationFormModal comments tab.
 * Importance: reviewer/author coordination timeline.
 * Refactor: move notification preview/recipient logic into shared helper hook.
 */
import { SendOutlined } from "@ant-design/icons"
import { Button, Input, Tag, Typography } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { sortBy } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"

import { evalCommentAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  EVAL_COMMENT_TYPE,
  EVAL_EVENT_TYPE,
  EvalCommentConstructor,
  type EvalEventType,
} from "#customTypes/composedTypes/evalCommentTypes"
import { EvaluationConstructor } from "#customTypes/composedTypes/evaluationTypes"
import {
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  NOTIFICATION_PRIORITY,
  NOTIFICATION_SUBTYPE,
  NOTIFICATION_TYPE,
} from "#customTypes/composedTypes/notificationTypes"
import { DatabaseObject } from "#customTypes/types"
import { useCreateNotification } from "#hooks/jotai/useCreateNotification"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import { useGetOneUser } from "#hooks/newHooks/useGetUser"
import {
  getEvalNotificationRecipient,
  getEventLabel,
} from "#utils/evalCommentHelpers"

dayjs.extend(relativeTime)

const { Text } = Typography

const SCommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 20px 8px 0;
`

const SComment = styled.div<{ $isOwn: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({ $isOwn }) => ($isOwn ? "#e6f4ff" : "#f5f5f5")};
  align-self: ${({ $isOwn }) => ($isOwn ? "flex-end" : "flex-start")};
  max-width: 85%;
`

const SEvent = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0;

  > div {
    width: 420px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`

const EVENT_TAG_COLORS: Record<EvalEventType, string> = {
  [EVAL_EVENT_TYPE.SUBMITTED]: "orange",
  [EVAL_EVENT_TYPE.SAVED_DRAFT]: "warning",
  [EVAL_EVENT_TYPE.APPROVED]: "success",
  [EVAL_EVENT_TYPE.REJECTED]: "error",
  [EVAL_EVENT_TYPE.PRINTED]: "blue",
  [EVAL_EVENT_TYPE.AUTHOR_ASSIGNED]: "default",
  [EVAL_EVENT_TYPE.APPROVER_ASSIGNED]: "default",
}

const getRoleLabel = (
  authorId: string,
  assignedTo?: string,
  approverId?: string
): string | null => {
  if (authorId === assignedTo) return "author"
  if (authorId === approverId) return "approver"
  return null
}

const CommentBubble = ({
  comment,
  isOwn,
  assignedTo,
  approverId,
}: {
  comment: EvalCommentConstructor
  isOwn: boolean
  assignedTo?: string
  approverId?: string
}) => {
  const author = useGetOneUser(comment.authorId)
  const timeAgo = dayjs(comment.createdAt).fromNow()
  const role = getRoleLabel(comment.authorId, assignedTo, approverId)
  const nameWithRole = `${author?.name ?? "Unknown"}${role ? ` (${role})` : ""}`

  if (comment.type === EVAL_COMMENT_TYPE.EVENT) {
    const isAssignment =
      comment.eventType === EVAL_EVENT_TYPE.AUTHOR_ASSIGNED ||
      comment.eventType === EVAL_EVENT_TYPE.APPROVER_ASSIGNED

    if (isAssignment) {
      return (
        <SEvent>
          <div>
            <Text
              type="secondary"
              style={{ fontSize: 11, fontStyle: "italic" }}
            >
              {comment.message || getEventLabel(comment.eventType!)} ·{" "}
              {nameWithRole} · {timeAgo}
            </Text>
          </div>
        </SEvent>
      )
    }

    const tagColor = comment.eventType
      ? EVENT_TAG_COLORS[comment.eventType]
      : "default"

    return (
      <SEvent>
        <div>
          <Tag
            color={tagColor}
            style={{
              fontSize: 11,
              lineHeight: "20px",
              margin: 0,
              width: 76,
              textAlign: "center",
            }}
          >
            {comment.eventType ? getEventLabel(comment.eventType) : "Event"}
          </Tag>
          <Text type="secondary" style={{ fontSize: 10 }}>
            {nameWithRole} · {timeAgo}
          </Text>
        </div>
      </SEvent>
    )
  }

  return (
    <SComment $isOwn={isOwn}>
      <Flex style={{ gap: 6, alignItems: "baseline" }}>
        <Text strong style={{ fontSize: 12 }}>
          {nameWithRole}
        </Text>
        <Text type="secondary" style={{ fontSize: 10 }}>
          {timeAgo}
        </Text>
      </Flex>
      <Text style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
        {comment.message}
      </Text>
    </SComment>
  )
}

interface EvalCommentsProps {
  evaluation: EvaluationConstructor
}

const EvalComments = ({ evaluation }: EvalCommentsProps) => {
  const { currentUserId } = useUserContext()
  const [draft, setDraft] = useState("")
  const [isSending, setIsSending] = useState(false)
  const { createNotification } = useCreateNotification()

  const queryConfig: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        evaluationId: { value: evaluation.id },
      },
    }),
    [evaluation.id]
  )

  const rawComments = useFilteredDocs<EvalCommentConstructor>(
    evalCommentAtoms,
    DOC_TYPES.EVAL_COMMENTS,
    queryConfig
  )

  const comments = useMemo(
    () => sortBy(rawComments, (c) => c.createdAt),
    [rawComments]
  )

  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = listRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }, [comments.length])

  const recipientId = useMemo(
    () =>
      currentUserId
        ? getEvalNotificationRecipient(
            currentUserId,
            evaluation.assignedTo,
            evaluation.approverId
          )
        : null,
    [currentUserId, evaluation.assignedTo, evaluation.approverId]
  )

  const handleSend = useCallback(async () => {
    if (!currentUserId || !draft.trim()) return

    setIsSending(true)
    try {
      const now = new Date().toISOString()

      const commentData: Omit<EvalCommentConstructor, "id"> = {
        evaluationId: evaluation.id,
        authorId: currentUserId,
        message: draft.trim(),
        createdAt: now,
        createdBy: currentUserId,
        lastUpdatedAt: now,
        lastUpdatedBy: currentUserId,
      }

      await Firebase.getInstance().addDocument<
        EvalCommentConstructor & DatabaseObject
      >(
        DOC_TYPES.EVAL_COMMENTS,
        commentData as Omit<EvalCommentConstructor & DatabaseObject, "id">
      )

      if (recipientId) {
        const preview =
          draft.trim().length > 80
            ? `${draft.trim().slice(0, 80)}...`
            : draft.trim()

        await createNotification({
          recipientId,
          type: NOTIFICATION_TYPE.EVALUATION,
          subtype: NOTIFICATION_SUBTYPE.EVAL_COMMENT,
          priority: NOTIFICATION_PRIORITY.NORMAL,
          header: "New eval comment",
          message: preview,
          metadata: { evaluationId: evaluation.id },
        })
      }

      setDraft("")
    } finally {
      setIsSending(false)
    }
  }, [currentUserId, draft, evaluation.id, recipientId, createNotification])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        void handleSend()
      }
    },
    [handleSend]
  )

  return (
    <Flex col style={{ height: "100%" }}>
      <SCommentList ref={listRef}>
        {comments.length === 0 && (
          <Text
            type="secondary"
            style={{ textAlign: "center", padding: "24px 0" }}
          >
            No comments yet. Start the conversation.
          </Text>
        )}
        {comments.map((comment) => (
          <CommentBubble
            key={comment.id}
            comment={comment}
            isOwn={comment.authorId === currentUserId}
            assignedTo={evaluation.assignedTo}
            approverId={evaluation.approverId}
          />
        ))}
      </SCommentList>

      <Flex
        style={{
          gap: 8,
          marginTop: 8,
          width: 600,
          marginLeft: "auto",
          paddingRight: 22,
        }}
      >
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a comment... (Enter to send, Shift+Enter for new line)"
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={isSending}
          disabled={!draft.trim()}
        />
      </Flex>
    </Flex>
  )
}

export default EvalComments
